import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  type ColumnMeta,
  SortingState,
  useReactTable,
} from '@tanstack/react-table';
import { Add, Information, Download } from '@carbon/react/icons';
import {
  Button,
  DataTableSkeleton,
  InlineNotification,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@carbon/react';
import { Date } from 'components/date';
import { DebouncedInput } from 'components/debounced-input';
import { EmptyState } from 'components/empty-state';
import ErrorToast from 'components/error-toast';
import { SuccessToast } from 'components/success-toast';
import { MIN_WIDTH } from 'components/table/column-actions';
import { ColumnFilter } from 'components/table/column-filter';
import { HiddenColumns } from 'components/table/hidden-columns';
import { TableActions } from 'components/table/table-actions';
import NoDataSvg from 'images/srp-illustration-data.svg';
import { useAtom, useSetAtom } from 'jotai';
import { wellboreGeometryWorkspaceSelectionsAtom } from 'components/pre-drill-workspace-launcher';
import { useColumnFilter } from 'lib/hooks/useColumnFilter';
import { useColumnSizing } from 'lib/hooks/useColumnSizing';
import { useColumnVisibility } from 'lib/hooks/useColumnVisibilityAtom';
import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { Wellbore } from 'types/api-responses';
import { formatMutationResource } from 'utils/misc';
import { formatNumber } from 'utils/number';
import { exportTableToXlsx, XlsxExportSheet } from 'utils/tables';

import {
  wellboresMutationAtom,
  wellboreGeometryExportMutationAtom,
} from './atoms';
import { GeometryStatusCell } from './geometry-status-cell';
import { isStickRow, WellborePointsRows } from './wellbore-points-rows';
import {
  buildActionsColumn,
  buildExpandColumn,
  buildPrimaryColumn,
  expandKey,
} from './wellbores-table-geometry-columns';
import { PerforationAdjustmentPopup } from './perforation-adjustment-popup';
import { StickLateralFactorPopup } from './stick-lateral-factor-popup';

import { FloatingTooltip } from 'components/floating-tooltip';
import { convertFactorToValue, headerWithTooltip } from './utils';
import type { PerforationAdjustmentValue } from './types';
import styles from './wellbores-table.module.scss';

const columnHelper = createColumnHelper<Wellbore>();

// A row that belongs to a wellbore geometry workspace (gates the Source + Verification
// columns): a permit-derived row has a predrillType; a saved edit, and a draft opened on a
// drilled bore, carry their own provenance. Read off the raw provenance rather than the
// label, which reads Survey / User Edited Survey for those (INS-12936 UAT item 2).
const WORKSPACE_PROVENANCE = ['user_edited', 'survey_draft'];
const isPreDrillRow = (row: Wellbore) =>
  row.predrillType !== null ||
  WORKSPACE_PROVENANCE.includes(row.geometrySource);

// 'N/A' on a drilled row means "not applicable / no data from source".
// On a predrill row the same geometry/depth/perforation cells are not absent
// — the geometry just hasn't been computed yet. Render '--' (codebase
// convention for "pending") on predrill rows so the distinction is visible
// at a glance.
const cellFallback = (row: Wellbore) => {
  return isPreDrillRow(row) ? '--' : 'N/A';
};

const columns = [
  columnHelper.accessor('uwi12', {
    id: 'uwi12',
    header: 'Wellbore (UWI12)',
    size: 150,
    cell: (info) => <span className={styles.cell}>{info.getValue()}</span>,
  }),
  columnHelper.accessor('uwi14', {
    id: 'uwi14',
    header: 'UWI14',
    size: 170,
    cell: (info) => <span className={styles.cell}>{info.getValue()}</span>,
  }),
  columnHelper.accessor('completionType', {
    id: 'completionType',
    header: 'Type',
    size: 120,
    cell: (info) => <span className={styles.cell}>{info.getValue()}</span>,
  }),
  // Provenance column: Survey / Interpreted / Estimated. Replaces the former
  // "Pre-Drill Target Zone" column — per product direction, now that
  // permit/pre-drill geometries surface in Subsurface Explorer and the team has
  // leaned away from the pre-drill workspace, this table carries geometry
  // provenance instead. Distinct from Type (bore classification).
  columnHelper.accessor('geometrySourceKind', {
    id: 'geometrySourceKind',
    header: 'Wellbore Geometry Type',
    size: 140,
    cell: (info) => <span className={styles.cell}>{info.getValue()}</span>,
  }),
  columnHelper.accessor('geometryStatus', {
    id: 'geometryStatus',
    header: 'Wellbore Geometry Status',
    size: 200,
    cell: (info) => (
      <GeometryStatusCell
        status={info.getValue()}
        comment={info.row.original.geometryComment}
        fallback={cellFallback(info.row.original)}
      />
    ),
  }),
  // The column ids predate the rename, so saved visibility and widths still apply.
  columnHelper.accessor('completionLengthFt', {
    id: 'perforatedLateralLengthFt',
    header: 'Completion Length (ft)',
    size: 200,
    meta: {
      getCellClassName: (info: {
        getValue: () => number | null;
        row: { original: Wellbore };
      }) => {
        const value = info.getValue();
        const completionSource = info.row.original.completionSource;
        return value !== null && completionSource === 'Estimated'
          ? styles.estimatedCell
          : undefined;
      },
    } as unknown as ColumnMeta<Wellbore, number | null>,
    cell: (info) => {
      const value = info.getValue();
      const completionSource = info.row.original.completionSource;

      if (value === null && completionSource === 'Estimated') {
        return (
          <FloatingTooltip
            content={
              <>
                <strong>Wellbore Geometry Error:</strong> the Estimated
                Completion Length could not be calculated for the given Wellbore
                Geometry.
              </>
            }
            placement="bottom"
          >
            <span className={styles.unavailableCell}>Unavailable</span>
          </FloatingTooltip>
        );
      }

      return (
        <span className={styles.cell}>
          {formatNumber(value, 0, 0, 'decimal', true, 'N/A', 'ft')}
        </span>
      );
    },
  }),
  columnHelper.accessor('completionSource', {
    id: 'perforationSource',
    header: headerWithTooltip('Completion Source', () => (
      <span className={styles.headerWithTooltip}>
        Completion Source
        <FloatingTooltip
          content="Defines whether Completion Length is cut by Insights from the well's perforation intervals, taken from the vendor perforated lateral, or estimated from the Survey Geometry when perforations are not available."
          placement="top"
        >
          <Information className={styles.headerInfoIcon} />
        </FloatingTooltip>
      </span>
    )),
    size: 170,
    // Completion source describes how completion length was derived
    // (Insights cut, vendor data or estimated from geometry). For a predrill row the well
    // hasn't been perforated yet and never will be at this stage — the
    // concept is not applicable, not pending, so render 'N/A' on both row
    // types instead of the predrill '--'.
    cell: (info) => (
      <span className={styles.cell}>{info.getValue() ?? 'N/A'}</span>
    ),
  }),
  columnHelper.accessor('measuredDepthFt', {
    id: 'measuredDepthFt',
    header: 'Measured Depth (ft)',
    size: 150,
    cell: (info) => (
      <span className={styles.cell}>
        {formatNumber(
          info.getValue(),
          0,
          0,
          'decimal',
          true,
          cellFallback(info.row.original),
          'ft'
        )}
      </span>
    ),
  }),
  columnHelper.accessor('kbElevationFt', {
    id: 'kbElevationFt',
    header: 'Kelly Bushing Elevation (KB, ft)',
    size: 130,
    cell: (info) => (
      <span className={styles.cell}>
        {formatNumber(
          info.getValue(),
          0,
          0,
          'decimal',
          true,
          cellFallback(info.row.original),
          'ft'
        )}
      </span>
    ),
  }),
  columnHelper.accessor('trueVerticalDepthFt', {
    id: 'trueVerticalDepthFt',
    header: 'True Vertical Depth (TVDSS, ft)',
    size: 220,
    cell: (info) => (
      <span className={styles.cell}>
        {formatNumber(
          info.getValue(),
          0,
          0,
          'decimal',
          true,
          cellFallback(info.row.original),
          'ft'
        )}
      </span>
    ),
  }),
  columnHelper.accessor('lastUpdatedAt', {
    id: 'lastUpdatedAt',
    header: 'Last Updated',
    size: 130,
    cell: (info) => (
      <Date
        className={styles.cell}
        date={info.getValue()}
        format="M/D/YYYY"
        fallback="N/A"
      />
    ),
  }),
];

interface WellboresTableProps {
  uwi: string;
  wellboresHref: string;
  wellboreGeometryExportHref?: string;
  initialPerforationFactor: number | null;
  /**
   * wells.stick_estimated_lateral_factor, edited beside the perforation factor. The editor is
   * Well-page only: it PATCHes the canonical well, which inside a Well Workspace would bypass
   * staging, so it is not rendered when `wellWorkspaceId` is set. Undefined until the well has
   * loaded — the column is NOT NULL, so a loaded well always carries one — and the editor waits
   * for it rather than showing a stand-in.
   */
  initialStickLateralFactor?: number;
  /**
   * Story 7 (INS-11588): the row action that opens the Wellbore Geometry tearsheet.
   * Given only inside a Well Workspace; the read-only well record has no row action
   * and its card banner names opening a workspace as the way in.
   */
  onEditGeometry?: (row: Wellbore) => void;
  /**
   * The row action that changes which workspace drives the row's geometry. Given only
   * inside a Well Workspace; the Well page shows the selected source and offers no way
   * to change it.
   */
  onChangeGeometrySource?: (row: Wellbore) => void;
  /**
   * The row action that reopens the LTD picker over an existing bore, replacing its
   * geometry in place. Rendered only for a bore the picker built (`geometrySource ===
   * 'ltd_composite'`) — see `buildActionsColumn`. Given only inside a Well Workspace.
   */
  onRebuildWellbore?: (row: Wellbore) => void;
  /**
   * The row action that removes a bore from the well. Given only inside a Well Workspace,
   * and only for a well authored there — the backend refuses the write for any other.
   *
   * Offered on ANY drawn bore, not only one the LTD picker built: the removal is of the
   * staged geometry, whatever produced it. Rendered on every row but a permit-derived
   * pre-drill one — see `buildActionsColumn`.
   */
  onRemoveWellbore?: (row: Wellbore) => void;
  /**
   * The bore the well's geography, units and promoted line derive from, so the table can
   * mark it. Null means authored but boreless; undefined means the host does not know
   * (an ingested well, where the backend serves no pointer) and the column is withheld.
   */
  primaryCompletionUwi14?: string | null;
  /**
   * The bores the well has actually DRAWN, from the backend's own `boreCompletionUwi14s`.
   *
   * Gates both the primary radio and the remove action. Not inferable from the row: an
   * authored bore is routinely both a drawn bore and a permit-stage row, so reading
   * `predrillType` would withhold the controls from exactly the rows they serve.
   */
  drawnBoreUwi14s?: string[];
  /**
   * Moves the primary pointer to a row's bore. Given only alongside a known
   * `primaryCompletionUwi14` — see `buildPrimaryColumn` for why a radio group with no
   * selection would be a lie rather than an empty state.
   */
  onSetPrimary?: (row: Wellbore) => void;
  /** The bore mid-switch, so only its own radio shows the pending state. */
  pendingPrimaryUwi14?: string | null;
  /** Holds the radio column while a switch is in flight. */
  settingPrimary?: boolean;
  /**
   * Adds a wellbore to a well authored in a workspace (Epic 3 Story 1). Given only there:
   * an ingested well arrives with its bores, so the Well page offers no way to author one
   * and its empty state stays a plain "none available".
   *
   * Adds ANOTHER one each time — a well may hold several bores, and the backend allocates
   * the next completion suffix per save. Replacing one is `onRebuildWellbore`.
   */
  onAddWellbore?: () => void;
  /**
   * Whether to say how many listed bores have a simplified (two-point stick)
   * geometry, and with which call to action: inside a workspace the reader can edit
   * here, on the read-only record they have to open a workspace first.
   */
  simplifiedGeometryBanner?: 'workspace' | 'readOnly';
  /**
   * Opens each row into its wellbore points behind a leading chevron. Off by default,
   * so a host that has not asked for it keeps the columns it has today.
   */
  expandableWellborePoints?: boolean;
  /** The Well Workspace hosting the table, sent so the server pins each bore to that
   *  workspace's own geometry (drafts included). Absent on the Well page. */
  wellWorkspaceId?: number;
}

// "Wellbore Geometry Source": the Well Workspace a pre-drill row was authored under,
// else its geometry workspace's name (UAT 26); "Vendor" for a drilled row and "System"
// for an estimated or stick row. Plain text; changing it is the Actions column's job.
const geometrySourceName = (row: Wellbore): string =>
  row.wellWorkspace?.name ??
  row.workspace?.name ??
  'Pre-Drill System Workspace';

const buildGeometrySourceColumn = () =>
  columnHelper.accessor((row) => geometrySourceName(row), {
    id: 'workspace',
    header: 'Wellbore Geometry Source',
    size: 220,
    cell: (info) => {
      const row = info.row.original;
      if (!isPreDrillRow(row)) {
        const label =
          row.geometrySourceKind === 'Estimated' || isStickRow(row)
            ? 'System'
            : 'Vendor';
        return <span className={styles.cell}>{label}</span>;
      }
      return <span className={styles.cell}>{geometrySourceName(row)}</span>;
    },
  });

const WELLBORES_TABLE_ID = 'wellbores-table';

const WELLBORE_GEOMETRY_EXPORT_HREF = '/queries/wellbores/export_geometry';

export const WellboresTable = ({
  uwi,
  wellboresHref,
  wellboreGeometryExportHref = WELLBORE_GEOMETRY_EXPORT_HREF,
  initialPerforationFactor,
  initialStickLateralFactor,
  onEditGeometry,
  onChangeGeometrySource,
  onRebuildWellbore,
  onRemoveWellbore,
  primaryCompletionUwi14,
  drawnBoreUwi14s,
  onSetPrimary,
  pendingPrimaryUwi14 = null,
  settingPrimary = false,
  onAddWellbore,
  simplifiedGeometryBanner,
  expandableWellborePoints = false,
  wellWorkspaceId,
}: WellboresTableProps) => {
  // The lane the query reads in: the Well Workspace's own rows when hosted there.
  // Memoised so the reads keyed on it fire on a change of host, not on every render.
  const workspaceLane = useMemo(() => {
    if (wellWorkspaceId === undefined) return {};
    return { well_workspace_id: wellWorkspaceId };
  }, [wellWorkspaceId]);
  // Rows opened to their points, by `expandKey` (it survives a reload).
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const toggleExpanded = useCallback((key: string) => {
    setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));
  }, []);
  const [wellboresData, setWellboresData] = useAtom(wellboresMutationAtom);
  const { loading, error } = formatMutationResource(wellboresData);
  // The displayed rows, seeded by each full-well load.
  const [rows, setRows] = useState<Wellbore[]>([]);

  const setWorkspaceSelections = useSetAtom(
    wellboreGeometryWorkspaceSelectionsAtom
  );

  // Seed shared pre-drill workspace selections (uwi14 → workspace id) from the
  // table's rows so the survey/profile queries have a default. Merge only bores
  // not already chosen, so an explicit pick made elsewhere isn't clobbered.
  // Permit-derived rows only: the atom is shared with the Well page, and a saved
  // edit's workspace can be a Well Workspace's unpromoted draft.
  useEffect(() => {
    setWorkspaceSelections((prev) => {
      const next = { ...prev };
      for (const row of rows) {
        if (
          row.predrillType !== null &&
          row.workspace &&
          !(row.uwi14 in next)
        ) {
          next[row.uwi14] = row.workspace.id;
        }
      }
      return next;
    });
  }, [rows, setWorkspaceSelections]);

  const [geometryExportData, setGeometryExportData] = useAtom(
    wellboreGeometryExportMutationAtom
  );
  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- exportData is read only through `typeof exportData` below
  const { loading: isExporting, data: exportData } =
    formatMutationResource(geometryExportData);

  useEffect(() => {
    if (!wellboresHref) return;
    setWellboresData({
      url: wellboresHref,
      type: 'POST',
      data: { query: { uwi: [uwi], include_predrill: true, ...workspaceLane } },
      onSuccess: (resp) => setRows(resp.results ?? []),
    });
  }, [wellboresHref, uwi, setWellboresData, workspaceLane]);

  const [sorting, setSorting] = useState<SortingState>([]);
  const [globalFilter, setGlobalFilter] = useState('');
  const [perforationAdjustment, setPerforationAdjustment] =
    useState<PerforationAdjustmentValue>(() =>
      convertFactorToValue(initialPerforationFactor)
    );
  // Seeded from the well and kept after an Apply; the popup re-seeds its draft from it.
  const [stickLateralFactor, setStickLateralFactor] = useState<
    number | undefined
  >(initialStickLateralFactor);
  useEffect(() => {
    setStickLateralFactor(initialStickLateralFactor);
  }, [initialStickLateralFactor, uwi]);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Update perforationAdjustment when initialPerforationFactor changes (e.g., after well data loads)
  useEffect(() => {
    setPerforationAdjustment(convertFactorToValue(initialPerforationFactor));
  }, [initialPerforationFactor]);

  const [columnVisibility, setColumnVisibility] =
    useColumnVisibility(WELLBORES_TABLE_ID);
  const [columnSizing, setColumnSizing] = useColumnSizing(WELLBORES_TABLE_ID);

  const {
    columnFilters,
    setColumnFilters,
    isColumnFilterActive,
    setIsColumnFilterActive,
  } = useColumnFilter(WELLBORES_TABLE_ID);

  const tableData = rows;

  const tableColumns = useMemo(() => {
    // Insert "Wellbore Geometry Source" right after the "Type" column.
    const typeIdx = columns.findIndex((c) => c.id === 'completionType');
    /* The primary radio leads the row — it is what the row IS, not something done to it,
       so it sits with the identifier rather than among the trailing actions. Withheld
       unless the host both knows the pointer and offers the switch; see
       `buildPrimaryColumn`. */
    const drawn = new Set(drawnBoreUwi14s ?? []);
    const canMarkPrimary =
      onSetPrimary !== undefined && primaryCompletionUwi14 !== undefined;
    const withSource = [
      ...(expandableWellborePoints
        ? [buildExpandColumn(expanded, toggleExpanded)]
        : []),
      ...(canMarkPrimary
        ? [
            buildPrimaryColumn({
              primaryUwi14: primaryCompletionUwi14 ?? null,
              drawn,
              onSetPrimary,
              pendingUwi14: pendingPrimaryUwi14,
              disabled: settingPrimary,
            }),
          ]
        : []),
      ...columns.slice(0, typeIdx + 1),
      buildGeometrySourceColumn(),
      ...columns.slice(typeIdx + 1),
    ];
    /* The edit action exists only where a host gave one — inside a Well Workspace — and
       it is what carries the whole column. Rebuild and remove ride along rather than being
       able to summon the column on their own: a host that offers either without edit would
       get an Actions column whose only button is absent on most rows. */
    return onEditGeometry
      ? [
          ...withSource,
          buildActionsColumn({
            onEditGeometry,
            onChangeGeometrySource,
            onRebuildWellbore,
            onRemoveWellbore,
            drawn,
          }),
        ]
      : withSource;
  }, [
    expanded,
    toggleExpanded,
    onEditGeometry,
    onChangeGeometrySource,
    onRebuildWellbore,
    onRemoveWellbore,
    primaryCompletionUwi14,
    drawnBoreUwi14s,
    onSetPrimary,
    pendingPrimaryUwi14,
    settingPrimary,
    expandableWellborePoints,
  ]);

  // Story 7's card banner: how many listed bores are two-point sticks. Clears once a
  // saved geometry carries depths, since that bore no longer reads as a stick.
  const stickCount = useMemo(
    () => tableData.filter(isStickRow).length,
    [tableData]
  );

  // Trigger export API call
  const handleDownloadDetailedSurvey = useCallback(() => {
    if (!tableData.length || isExporting) return;

    setGeometryExportData({
      url: wellboreGeometryExportHref,
      type: 'POST',
      data: { uwi: [uwi] },
      onSuccess: (responseData: typeof exportData) => {
        if (!responseData?.results?.length) {
          setErrorMessage('No survey geometry data available for export');
          return;
        }

        const sheets: XlsxExportSheet[] = [];
        const surveyColumns = [
          { key: 'wellPoint', header: 'Well Point' },
          { key: 'x', header: 'X' },
          { key: 'y', header: 'Y' },
          { key: 'md', header: 'MD' },
          { key: 'tvdss', header: 'TVDSS' },
        ];

        responseData.results.forEach((wellbore) => {
          const surveyGeometryPoints = wellbore.surveyGeometryPoints;
          if (!surveyGeometryPoints) return;

          // Survey Geometry tab
          if (surveyGeometryPoints.surveyGeometry?.length > 0) {
            sheets.push({
              name: `${wellbore.uwi14} Survey Data`,
              columns: surveyColumns,
              data: surveyGeometryPoints.surveyGeometry,
            });
          }

          // Perforated Lateral tab
          if (surveyGeometryPoints.perforatedLateral?.length > 0) {
            sheets.push({
              name: `${wellbore.uwi14} Perf Data`,
              columns: surveyColumns,
              data: surveyGeometryPoints.perforatedLateral,
            });
          }
        });

        if (sheets.length === 0) {
          setErrorMessage('No survey geometry data available for export');
          return;
        }

        try {
          exportTableToXlsx({
            filename: `Well_Geometry_${uwi}`,
            sheets,
          });
          setSuccessMessage('Survey data exported successfully');
        } catch (err) {
          setErrorMessage('Failed to export survey data');
        }
      },
      onError: () => {
        setErrorMessage('Failed to export survey data');
      },
    });
  }, [
    tableData,
    uwi,
    wellboreGeometryExportHref,
    isExporting,
    setGeometryExportData,
  ]);

  // Show download button if there are wellbores - the actual data availability
  // will be determined when the export API is called
  const hasWellbores = useMemo(() => {
    return tableData.length > 0;
  }, [tableData]);

  const table = useReactTable({
    data: tableData,
    columns: tableColumns,
    columnResizeMode: 'onChange',
    defaultColumn: {
      minSize: MIN_WIDTH,
    },
    state: {
      sorting,
      globalFilter,
      columnSizing,
      columnVisibility,
      columnFilters,
    },
    onColumnFiltersChange: setColumnFilters,
    onColumnSizingChange: setColumnSizing,
    onColumnVisibilityChange: setColumnVisibility,
    onSortingChange: setSorting,
    onGlobalFilterChange: setGlobalFilter,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    meta: {
      reset: {
        resetCustomizations: () => {
          setIsColumnFilterActive(false);
        },
      },
    },
  });

  const isColumnVisible = table.getVisibleLeafColumns().length > 0;

  const renderContent = () => {
    if (loading) {
      return (
        <DataTableSkeleton
          data-testid="wellbores-table-skeleton"
          columnCount={columns.length}
          rowCount={3}
          showHeader={false}
          showToolbar={false}
        />
      );
    }

    if (!loading && error?.type) {
      return (
        <InlineNotification
          kind="error"
          title="Error"
          subtitle="Failed to load wellbores data"
          lowContrast
          hideCloseButton
        />
      );
    }

    if (tableData.length === 0) {
      // A well AUTHORED in a workspace legitimately has no bore yet — that is the state it
      // is created in, not a failure to load one. It says what the geometry unlocks,
      // because until there is one the well has no location-derived attributes, no unit to
      // assign, and cannot be promoted.
      // An authored well falls THROUGH to the table: the mock keeps the column headers and
      // says "no wellbores yet" beneath them, rather than replacing the surface — the
      // reader is about to add one, and the columns tell them what they are adding.
      if (!onAddWellbore) {
        return (
          <EmptyState
            icon={NoDataSvg}
            headerText="No wellbores available for this well"
          />
        );
      }
    }

    return (
      <div className={styles.tableWrapper}>
        {simplifiedGeometryBanner && stickCount > 0 && (
          <InlineNotification
            kind="warning"
            lowContrast
            hideCloseButton
            className={styles.banner}
            title={`${stickCount} of ${tableData.length} ${
              tableData.length === 1 ? 'wellbore has' : 'wellbores have'
            } a simplified geometry.`}
            subtitle={
              simplifiedGeometryBanner === 'workspace'
                ? 'Edit the wellbore geometry to add points and derive depths.'
                : 'Open a well workspace to edit the wellbore geometry, add points, and derive depths.'
            }
          />
        )}
        {onAddWellbore && (
          <div className={styles.addWellboreRow}>
            <p className={styles.addWellboreNote}>
              {tableData.length === 0
                ? 'This well has no wellbore geometry. Add one to calculate its geographic attributes, assign a unit, and promote it.'
                : "Editing a wellbore's geometry recalculates the well's geographic attributes."}
            </p>
            <Button
              kind="tertiary"
              size="md"
              renderIcon={Add}
              onClick={onAddWellbore}
            >
              Add wellbore
            </Button>
          </div>
        )}
        <div className={styles.tableContext}>
          <div className={styles.searchArea}>
            <DebouncedInput
              className={styles.searchField}
              value={globalFilter ?? ''}
              onChange={(value) => setGlobalFilter(String(value).trim())}
              placeholder="Search columns..."
            />
          </div>
          <PerforationAdjustmentPopup
            uwi={uwi}
            value={perforationAdjustment}
            onChange={setPerforationAdjustment}
            onApplySuccess={(timestamp) => {
              setSuccessMessage(
                `The Estimated Perforated Lateral Length Adjustment Factor has been updated for the well. ${timestamp}`
              );
              setWellboresData({
                url: wellboresHref,
                type: 'POST',
                data: {
                  query: {
                    uwi: [uwi],
                    include_predrill: true,
                    ...workspaceLane,
                  },
                },
                onSuccess: (resp) => setRows(resp.results ?? []),
              });
            }}
            onApplyError={(message) => {
              setErrorMessage(message);
            }}
          />
          {/* Well page only, deliberately: the popup PATCHes the canonical `wells` row, so inside
              a Well Workspace it would bypass staging (no Compare entry, nothing to promote). */}
          {wellWorkspaceId === undefined &&
            stickLateralFactor !== undefined && (
              // Keyed by uwi so a table reused for another well never carries this well's draft.
              <StickLateralFactorPopup
                key={uwi}
                uwi={uwi}
                value={stickLateralFactor}
                onChange={setStickLateralFactor}
                onApplySuccess={(timestamp) => {
                  setSuccessMessage(
                    `The Stick Estimated Lateral Factor has been updated for the well. ${timestamp}`
                  );
                }}
                onApplyError={setErrorMessage}
              />
            )}
          <TableActions
            table={table}
            isColumnFilterActive={isColumnFilterActive}
            setIsColumnFilterActive={(active) =>
              setIsColumnFilterActive(active)
            }
          />
          {hasWellbores && (
            <FloatingTooltip
              content={
                isExporting ? 'Exporting...' : 'Download Detailed Survey Data'
              }
              placement="top"
            >
              <Button
                kind="ghost"
                size="sm"
                hasIconOnly
                renderIcon={Download}
                onClick={handleDownloadDetailedSurvey}
                className={styles.downloadButton}
                data-testid="download-survey-button"
                disabled={isExporting}
              />
            </FloatingTooltip>
          )}
        </div>
        {isColumnVisible ? (
          <div className={styles.tableContainer}>
            <Table size="sm">
              <TableHead>
                {table.getHeaderGroups().map((headerGroup) => (
                  <>
                    <TableRow key={headerGroup.id}>
                      {headerGroup.headers.map((header) => (
                        <TableHeader
                          key={header.id}
                          onClick={header.column.getToggleSortingHandler()}
                          style={{
                            cursor: 'pointer',
                            width: header.column.getSize(),
                          }}
                        >
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                          {{
                            asc: ' ↑',
                            desc: ' ↓',
                          }[header.column.getIsSorted() as string] ?? null}
                        </TableHeader>
                      ))}
                    </TableRow>
                    {isColumnFilterActive ? (
                      <TableRow key={`${headerGroup.id}-filter`}>
                        <ColumnFilter
                          headers={headerGroup.headers}
                          pinStyleGenerator={() => ({})}
                        />
                      </TableRow>
                    ) : null}
                  </>
                ))}
              </TableHead>
              <TableBody>
                {table.getRowModel().rows.map((row) => (
                  <Fragment key={row.id}>
                    <TableRow>
                      {row.getVisibleCells().map((cell) => {
                        const meta = cell.column.columnDef.meta as
                          | {
                              getCellClassName?: (
                                info: unknown
                              ) => string | undefined;
                            }
                          | undefined;
                        const cellClassName = meta?.getCellClassName?.(
                          cell.getContext()
                        );
                        return (
                          <TableCell
                            key={cell.id}
                            className={cellClassName}
                            style={{ width: cell.column.getSize() }}
                          >
                            {flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext()
                            )}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                    {expandableWellborePoints &&
                      expanded[expandKey(row.original)] && (
                        <TableRow>
                          <TableCell
                            className={styles.pointsCell}
                            colSpan={row.getVisibleCells().length}
                          >
                            <WellborePointsRows row={row.original} />
                          </TableCell>
                        </TableRow>
                      )}
                  </Fragment>
                ))}
              </TableBody>
            </Table>
          </div>
        ) : (
          <HiddenColumns className={styles.hiddenColumn} />
        )}
      </div>
    );
  };

  return (
    <>
      {successMessage && (
        <SuccessToast
          message="Success"
          subtitle={successMessage}
          onClose={() => {
            setSuccessMessage(null);
            return true;
          }}
        />
      )}
      {errorMessage && (
        <ErrorToast
          message={errorMessage}
          onClose={() => {
            setErrorMessage(null);
            return true;
          }}
        />
      )}
      {renderContent()}
    </>
  );
};

export default WellboresTable;
